import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { userService } from '../../../_services';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import DateRangePicker from '@/components/ui/date-range-picker';
import Pagination from '@/components/ui/pagination';
import { Loader2, Search, ArrowLeft, Receipt, ExternalLink } from 'lucide-react';

// ─── Mobile card ─────────────────────────────────────────────────────────────
function NLSOrderCard({ order, getStatusVariant, onClick }) {
    return (
        <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={onClick}
        >
            <CardContent className="p-4 space-y-3">
                {/* Header: name + status badge */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{order.userName || '-'}</p>
                        <p className="text-xs text-muted-foreground truncate">{order.fullName || '-'}</p>
                    </div>
                    <Badge variant={getStatusVariant(order.orderStatus)} className="shrink-0 pointer-events-none">
                        {order.orderStatus || 'Unknown'}
                    </Badge>
                </div>

                {/* Detail grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-t pt-3">
                    <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium mt-0.5 truncate">{order.email || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium mt-0.5">{order.phone || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Order Date</p>
                        <p className="font-medium mt-0.5">
                            {order.orderDate ? moment(order.orderDate).format('MM/DD/YYYY') : '-'}
                        </p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * NLS Rx Marketplace Orders Report
 * Modernized admin report for viewing NLS marketplace orders
 */
function NLSRxOrdersReport({ hideNLSRxSection }) {
    // Date range state — stored as YYYY-MM-DD strings
    const [startDate, setStartDate] = useState('2025-01-30');
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [ordersList, setOrdersList] = useState([]);
    const [ordersListTemp, setOrdersListTemp] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);
    const [pageOfItems, setPageOfItems] = useState([]);

    // Dialog state
    const [showOrderDialog, setShowOrderDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetails, setOrderDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Fetch orders on mount and date change
    useEffect(() => {
        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        fetchOrders(userId, startDate, endDate);
    }, []);

    const fetchOrders = async (userId, start, end) => {
        setLoading(true);
        try {
            const apidata = await userService.FetchNLSPaymentsAdmin(userId, start, end);

            if (apidata && apidata.length > 0) {
                setOrdersList(apidata);
                setOrdersListTemp(apidata);
            } else {
                setOrdersList([]);
                setOrdersListTemp([]);
            }
        } catch (err) {
            console.error('Error fetching NLS orders:', err);
            setOrdersList([]);
            setOrdersListTemp([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle date range change — onApply fires (event, picker) with moment objects
    const handleDateChange = (event, picker) => {
        const newStartDate = picker.startDate.format('YYYY-MM-DD');
        const newEndDate = picker.endDate.format('YYYY-MM-DD');
        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setSearchText('');

        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        fetchOrders(userId, newStartDate, newEndDate);
    };

    // Handle search filter
    const handleSearch = (value) => {
        setSearchText(value);

        if (!value || value.length === 0) {
            setOrdersList(ordersListTemp);
            return;
        }

        const searchLower = value.toLowerCase();
        const filtered = ordersListTemp.filter(order => {
            return (
                (order.email && order.email.toLowerCase().includes(searchLower)) ||
                (order.fullName && order.fullName.toLowerCase().includes(searchLower)) ||
                (order.userName && order.userName.toLowerCase().includes(searchLower)) ||
                (order.phone && order.phone.toLowerCase().includes(searchLower)) ||
                (order.orderStatus && order.orderStatus.toLowerCase().includes(searchLower))
            );
        });

        setOrdersList(filtered.length > 0 ? filtered : []);
    };

    // Handle order details popup
    const handleShowOrderDetails = async (order) => {
        setSelectedOrder(order);
        setShowOrderDialog(true);
        setLoadingDetails(true);

        if (order.orderId && order.orderId !== '-') {
            try {
                const apidata = await userService.FetchNLSPaymentsOrderAdmin(order.orderId);
                setOrderDetails(apidata || []);
            } catch (err) {
                console.error('Error fetching order details:', err);
                setOrderDetails([]);
            } finally {
                setLoadingDetails(false);
            }
        } else if (order.orderId === '-') {
            // Handle error status
            try {
                const statusData = JSON.parse(order.orderStatus);
                setOrderDetails(statusData);
            } catch (err) {
                setOrderDetails([]);
            }
            setLoadingDetails(false);
        } else {
            setOrderDetails([]);
            setLoadingDetails(false);
        }
    };

    // Get status badge variant
    const getStatusVariant = (status) => {
        if (!status) return 'secondary';
        const statusLower = status.toLowerCase();
        if (statusLower.includes('success') || statusLower.includes('completed')) return 'success';
        if (statusLower.includes('pending')) return 'warning';
        if (statusLower.includes('failed') || statusLower.includes('error')) return 'destructive';
        return 'secondary';
    };

    return (
        <div className="w-full space-y-6">
            {/* Back Button */}
            <Button variant="outline" onClick={hideNLSRxSection} className="self-start">
                <ArrowLeft className="h-4 w-4" />
                Back to Reports
            </Button>

            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">NLS Rx Marketplace Orders</h1>

            {/* Filters */}
            <Card>
                <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search */}
                        <div className="relative flex-1 sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Search by name, email, phone, or status..."
                                value={searchText}
                                onChange={(e) => handleSearch(e.target.value)}
                                disabled={loading}
                                className="pl-9 pr-9"
                            />
                        </div>

                        {/* Date Range */}
                        <DateRangePicker
                            startDate={moment(startDate)}
                            endDate={moment(endDate)}
                            onApply={handleDateChange}
                            displayMode="unified"
                            disabled={loading}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* ── Desktop table (md+) ───────────────────────────────────────────── */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="text-primary font-semibold">User Name</TableHead>
                                            <TableHead className="text-primary font-semibold">Full Name</TableHead>
                                            <TableHead className="text-primary font-semibold">Email</TableHead>
                                            <TableHead className="text-primary font-semibold">Phone</TableHead>
                                            <TableHead className="text-primary font-semibold">Order Date</TableHead>
                                            <TableHead className="text-primary font-semibold">Order Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {(searchText ? ordersList : pageOfItems).length > 0 ? (
                                            (searchText ? ordersList : pageOfItems).map((order, index) => (
                                                <TableRow
                                                    key={index}
                                                    className="cursor-pointer hover:bg-muted"
                                                    onClick={() => handleShowOrderDetails(order)}
                                                >
                                                    <TableCell className="text-primary">{order.userName || '-'}</TableCell>
                                                    <TableCell className="text-primary">{order.fullName || '-'}</TableCell>
                                                    <TableCell className="text-primary">{order.email || '-'}</TableCell>
                                                    <TableCell className="text-primary">{order.phone || '-'}</TableCell>
                                                    <TableCell className="text-primary">
                                                        {order.orderDate ? moment(order.orderDate).format('MM/DD/YYYY') : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={getStatusVariant(order.orderStatus)} className="pointer-events-none">
                                                            {order.orderStatus || 'Unknown'}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-primary/70">
                                                    No records found
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            {!searchText && ordersList.length > 0 && (
                                <div className="p-4 border-t">
                                    <Pagination
                                        items={ordersList}
                                        onChangePage={setPageOfItems}
                                        pageSize={10}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ── Mobile cards (<md) ────────────────────────────────────────────── */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <Card>
                        <CardContent className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : (searchText ? ordersList : pageOfItems).length > 0 ? (
                    (searchText ? ordersList : pageOfItems).map((order, index) => (
                        <NLSOrderCard
                            key={index}
                            order={order}
                            getStatusVariant={getStatusVariant}
                            onClick={() => handleShowOrderDetails(order)}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center py-8 text-primary/70">
                            No records found
                        </CardContent>
                    </Card>
                )}

                {!searchText && ordersList.length > 0 && (
                    <div className="pt-2">
                        <Pagination
                            items={ordersList}
                            onChangePage={setPageOfItems}
                            pageSize={10}
                        />
                    </div>
                )}
            </div>

            {/* Order Details Dialog */}
            <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
                <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                        <DialogTitle className="text-primary">
                            {selectedOrder?.userName || 'Order Details'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="max-h-[400px] overflow-y-auto">
                        {loadingDetails ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-accent" />
                            </div>
                        ) : selectedOrder?.orderId && selectedOrder.orderId !== '-' ? (
                            orderDetails.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Product Name</TableHead>
                                            <TableHead>Quantity</TableHead>
                                            <TableHead>Price</TableHead>
                                            <TableHead>Receipt</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {orderDetails.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell className="text-primary">{item.productName || '-'}</TableCell>
                                                <TableCell className="text-primary">{item.productQuantity || '-'}</TableCell>
                                                <TableCell className="text-primary">{item.productPrice || '-'}</TableCell>
                                                <TableCell>
                                                    {item.paymentReceipt && index === 0 ? (
                                                        <a
                                                            href={item.paymentReceipt}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-accent hover:text-accent/80"
                                                        >
                                                            <Receipt className="h-4 w-4" />
                                                            <span className="text-sm">View Receipt</span>
                                                            <ExternalLink className="h-3 w-3" />
                                                        </a>
                                                    ) : '-'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="text-center py-8 text-primary/70">No details found</div>
                            )
                        ) : selectedOrder?.orderId === '-' && orderDetails?.errors ? (
                            <div className="space-y-2">
                                {orderDetails.errors.map((error, index) =>
                                    Object.entries(error).map(([key, value]) => (
                                        <div key={`${index}-${key}`} className="grid grid-cols-2 gap-4 p-2 border-b">
                                            <div className="font-medium text-primary">{key}</div>
                                            <div className="text-primary">{value}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-primary/70">No details available</div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default NLSRxOrdersReport;
